import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, timeout } from 'rxjs';
import { DrawerModule } from 'primeng/drawer';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmationService, MessageService } from 'primeng/api';

import {
  EquipamentoService,
  PostoService,
  TipoEquipService,
} from '@core/services/domain-services';
import {
  EnumEstadoEquipamento,
  Equipamento,
  EquipamentoPropriedadeValor,
  Posto,
} from '@core/models/models';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { EquipmentPhotoComponent } from '../../../../equipamentos/components/equipment-photo/equipment-photo.component';

interface TipoEquipamentoPosto {
  id: number;
  tipoEquipamentoId: number;
  tipoEquipamentoDescricao: string;
  quantidade: number;
}

interface EquipamentoNecessidade {
  id: number;
  idPosto: number;
  nomePosto: string;
  equipamentoId: number;
  nomeEquipamento: string;
  quantidade: number;
  tipoEquipamentoId: number;
  tipoEquipamento: string;
  codigoInterno?: string;
  marca?: string;
  modelo?: string;
  estado?: string;
}

interface EquipamentoDisponivel extends Equipamento {
  id: number;
  propriedades?: EquipamentoPropriedadeValor[];
  ativo?: boolean;
}

interface NecessidadeItem {
  equipamentoId: number;
  quantidade: number;
  equipamento: EquipamentoDisponivel;
}

type CoverageFilter = 'all' | 'covered' | 'partial' | 'missing';
type EquipmentKind = 'vehicle' | 'weapon' | 'other';

interface EquipmentDetail {
  icon: string;
  label: string;
  value: string;
}

@Component({
  selector: 'app-posto-equipamentos-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DrawerModule,
    SkeletonModule,
    TooltipModule,
    InputTextModule,
    PaginatorControlComponent,
    EquipmentPhotoComponent,
  ],
  templateUrl: './posto-equipamentos-tab.component.html',
  styleUrl: './posto-equipamentos-tab.component.scss',
})
export class PostoEquipamentosTabComponent implements OnInit, OnChanges {
  @Input() postoId!: number;
  @Input() posto: Posto | null = null;
  @Output() countChange = new EventEmitter<number>();

  private readonly postoSvc = inject(PostoService);
  private readonly msg = inject(MessageService);
  private readonly confirm = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly equiService = inject(EquipamentoService);
  private readonly tipoEquiService = inject(TipoEquipService);

  loading = false;
  tiposEquipamentos: any[] = [];
  equipamentos: EquipamentoNecessidade[] = [];
  tiposDoPosto: TipoEquipamentoPosto[] = [];
  coberturaPorTipoMap = new Map<number, number>();

  activeTab: 'all' | number = 'all';
  searchQuery = '';
  page = 1;
  pageSize = 10;

  showFiltrosDrawer = false;
  filtroTipoId: number | null = null;
  filtroTipoTemp: number | null = null;
  filtroCobertura: CoverageFilter = 'all';
  filtroCoberturaTemp: CoverageFilter = 'all';
  searchQueryTemp = '';

  readonly coberturaOptions: Array<{
    value: CoverageFilter;
    label: string;
    description: string;
    icon: string;
  }> = [
    { value: 'all', label: 'Todas', description: 'Sem filtro de cobertura', icon: 'ri-apps-line' },
    { value: 'covered', label: 'Cobertas', description: 'Quantidade totalmente satisfeita', icon: 'ri-checkbox-circle-line' },
    { value: 'partial', label: 'Parciais', description: 'Ainda faltam alguns equipamentos', icon: 'ri-time-line' },
    { value: 'missing', label: 'Sem cobertura', description: 'Nenhum equipamento atribuído', icon: 'ri-error-warning-line' },
  ];

  showDrawer = false;
  drawerTipoSelecionado: TipoEquipamentoPosto | null = null;
  drawerEquipamentos: EquipamentoDisponivel[] = [];
  drawerSearch = '';
  drawerSeleccionados: NecessidadeItem[] = [];
  drawerSeleccionadosMap = new Map<number, NecessidadeItem>();
  drawerLoading = false;
  drawerSaving = false;
  drawerPage = 1;
  drawerPageSize = 5;
  drawerDetalheExpandidoId: number | null = null;

  readonly skeletons = [1, 2, 3, 4];

  ngOnInit(): void {
    this.loadEquiPostoNecessidade();
    this.loadTipoEquipamentos();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['posto'] && this.posto) {
      this.tiposDoPosto = (this.posto as any).tiposEquipamentos ?? [];
      this.buildCoberturaMap();
      this.cdr.markForCheck();
    }
  }

  load(): void {
    this.postoSvc
      .getById(this.postoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.tiposDoPosto = Array.isArray(res?.tiposEquipamentos)
            ? res.tiposEquipamentos
            : [];
          this.buildCoberturaMap();
          this.cdr.markForCheck();
        },
        error: () => this.cdr.markForCheck(),
      });
  }

  loadEquiPostoNecessidade(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.postoSvc
      .listarEquiNecessidadeByPosto(this.postoId)
      .pipe(
        timeout({ first: 10000 }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          this.equipamentos = this.extractData<EquipamentoNecessidade>(res);
          this.buildCoberturaMap();
          this.countChange.emit(this.equipamentos.length);
          this.ensureValidPage();
          this.cdr.markForCheck();
        },
        error: () => {
          this.equipamentos = [];
          this.buildCoberturaMap();
          this.countChange.emit(0);
          this.msg.add({
            severity: 'error',
            summary: 'Equipamentos indisponíveis',
            detail: 'Não foi possível carregar os equipamentos deste posto.',
            life: 3500,
          });
          this.cdr.markForCheck();
        },
      });
  }

  loadTipoEquipamentos(): void {
    this.tipoEquiService
      .getAll({ pageSize: 100 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.tiposEquipamentos = this.extractData<any>(res);
          this.cdr.markForCheck();
        },
        error: () => this.cdr.markForCheck(),
      });
  }

  private extractData<T>(response: any): T[] {
    if (Array.isArray(response)) return response as T[];
    return Array.isArray(response?.data) ? (response.data as T[]) : [];
  }

  private buildCoberturaMap(): void {
    this.coberturaPorTipoMap.clear();
    for (const equipamento of this.equipamentos) {
      const tipoId = equipamento.tipoEquipamentoId;
      this.coberturaPorTipoMap.set(
        tipoId,
        (this.coberturaPorTipoMap.get(tipoId) ?? 0) +
          (equipamento.quantidade ?? 1),
      );
    }
  }

  get tiposUnicos(): TipoEquipamentoPosto[] {
    const map = new Map<number, TipoEquipamentoPosto>();
    for (const tipo of this.tiposDoPosto) {
      const current = map.get(tipo.tipoEquipamentoId);
      map.set(tipo.tipoEquipamentoId, current
        ? { ...current, quantidade: current.quantidade + tipo.quantidade }
        : { ...tipo });
    }
    return Array.from(map.values());
  }

  get activeTipo(): TipoEquipamentoPosto | null {
    if (this.activeTab === 'all') return null;
    return this.tiposUnicos.find((tipo) => tipo.tipoEquipamentoId === this.activeTab) ?? null;
  }

  get necessidadesFiltradas(): EquipamentoNecessidade[] {
    const query = this.normalize(this.searchQuery);

    return this.equipamentos.filter((equipamento) => {
      if (this.activeTab !== 'all' && equipamento.tipoEquipamentoId !== this.activeTab) return false;
      if (this.filtroTipoId !== null && equipamento.tipoEquipamentoId !== this.filtroTipoId) return false;
      if (this.filtroCobertura !== 'all' && this.getCoverageState(equipamento.tipoEquipamentoId) !== this.filtroCobertura) return false;
      if (!query) return true;

      return [
        equipamento.nomeEquipamento,
        equipamento.tipoEquipamento,
        equipamento.codigoInterno,
        equipamento.marca,
        equipamento.modelo,
        equipamento.equipamentoId,
      ].some((value) => this.normalize(value).includes(query));
    });
  }

  get necessidadesPaginadas(): EquipamentoNecessidade[] {
    const start = (this.page - 1) * this.pageSize;
    return this.necessidadesFiltradas.slice(start, start + this.pageSize);
  }

  get totalFiltrado(): number {
    return this.necessidadesFiltradas.length;
  }

  get activeFiltersCount(): number {
    return Number(!!this.searchQuery.trim()) +
      Number(this.filtroTipoId !== null) +
      Number(this.filtroCobertura !== 'all');
  }

  get hasActiveFilters(): boolean {
    return this.activeFiltersCount > 0;
  }

  get coberturaGlobal(): { alocado: number; necessario: number; percent: number } {
    const necessario = this.tiposUnicos.reduce((sum, tipo) => sum + (tipo.quantidade ?? 0), 0);
    const alocado = this.tiposUnicos.reduce(
      (sum, tipo) => sum + (this.coberturaPorTipoMap.get(tipo.tipoEquipamentoId) ?? 0),
      0,
    );
    return {
      alocado,
      necessario,
      percent: necessario > 0 ? Math.min(Math.round((alocado / necessario) * 100), 100) : 0,
    };
  }

  getCoberturaDoTipo(tipoId: number): { alocado: number; necessario: number; percent: number } {
    const necessario = this.tiposUnicos.find((tipo) => tipo.tipoEquipamentoId === tipoId)?.quantidade ?? 0;
    const alocado = this.coberturaPorTipoMap.get(tipoId) ?? 0;
    return {
      alocado,
      necessario,
      percent: necessario > 0 ? Math.min(Math.round((alocado / necessario) * 100), 100) : 0,
    };
  }

  getCoverageState(tipoId: number): Exclude<CoverageFilter, 'all'> {
    const { alocado, necessario } = this.getCoberturaDoTipo(tipoId);
    if (necessario > 0 && alocado >= necessario) return 'covered';
    if (alocado > 0) return 'partial';
    return 'missing';
  }

  getCoverageLabel(tipoId: number): string {
    const state = this.getCoverageState(tipoId);
    return state === 'covered' ? 'Coberta' : state === 'partial' ? 'Parcial' : 'Em falta';
  }

  getCobBadgeClass(tipoId: number): string {
    const state = this.getCoverageState(tipoId);
    return state === 'covered' ? 'ok' : state === 'partial' ? 'warn' : 'err';
  }

  getCoberturaPercent(tipo: TipoEquipamentoPosto): number {
    return this.getCoberturaDoTipo(tipo.tipoEquipamentoId).percent;
  }

  getCobBarColor(percent: number): string {
    if (percent >= 100) return '#22c55e';
    if (percent > 0) return '#f59e0b';
    return '#ef4444';
  }

  openFiltrosDrawer(): void {
    this.filtroTipoTemp = this.filtroTipoId;
    this.filtroCoberturaTemp = this.filtroCobertura;
    this.searchQueryTemp = this.searchQuery;
    this.showFiltrosDrawer = true;
    this.cdr.detectChanges();
  }

  aplicarFiltros(): void {
    this.filtroTipoId = this.filtroTipoTemp;
    this.filtroCobertura = this.filtroCoberturaTemp;
    this.searchQuery = this.searchQueryTemp.trim();
    this.page = 1;
    this.showFiltrosDrawer = false;
    this.cdr.markForCheck();
  }

  limparFiltros(closeDrawer = true): void {
    this.filtroTipoTemp = null;
    this.filtroTipoId = null;
    this.filtroCoberturaTemp = 'all';
    this.filtroCobertura = 'all';
    this.searchQueryTemp = '';
    this.searchQuery = '';
    this.page = 1;
    if (closeDrawer) this.showFiltrosDrawer = false;
    this.cdr.markForCheck();
  }

  removeSearchFilter(): void {
    this.searchQuery = '';
    this.searchQueryTemp = '';
    this.page = 1;
  }

  /**
   * REGRA DE NEGÓCIO: Desabilita as abas de tipo do topo se houver seleção no Drawer
   */
  isTabPillDisabled(tipoId: number): boolean {
    if (this.showDrawer && this.drawerTipoSelecionado !== null) {
      return this.drawerTipoSelecionado.tipoEquipamentoId !== tipoId;
    }
    return false;
  }

  /**
   * REGRA DE NEGÓCIO: Bloqueia a seleção de outro tipo no drawer se já houver um tipo selecionado
   */
  isDrawerOptionDisabled(tipoId: number): boolean {
    if (this.drawerTipoSelecionado !== null) {
      return this.drawerTipoSelecionado.tipoEquipamentoId !== tipoId;
    }
    return false;
  }

  onTabChange(tab: 'all' | number): void {
    this.activeTab = tab;
    this.page = 1;
    this.cdr.markForCheck();
  }

  onPageChange(event: { page?: number; rows?: number }): void {
    const rows = event.rows ?? this.pageSize;
    this.page = rows !== this.pageSize ? 1 : (event.page ?? 0) + 1;
    this.pageSize = rows;
    this.cdr.markForCheck();
  }

  private ensureValidPage(): void {
    const lastPage = Math.max(1, Math.ceil(this.totalFiltrado / this.pageSize));
    this.page = Math.min(this.page, lastPage);
  }

  openDrawer(tipoId?: number): void {
    this.resetDrawerState(false);
    this.drawerTipoSelecionado = tipoId
      ? this.tiposUnicos.find((tipo) => tipo.tipoEquipamentoId === tipoId) ?? null
      : null;

    this.showDrawer = true;
    this.cdr.detectChanges();

    if (this.drawerTipoSelecionado) {
      this.loadEquipamentosDoTipo(this.drawerTipoSelecionado.tipoEquipamentoId);
    }
  }

  openDrawerForTab(): void {
    this.openDrawer(this.activeTab !== 'all' ? this.activeTab : undefined);
  }

  closeDrawer(): void {
    if (this.drawerSaving) return;
    this.showDrawer = false;
  }

  resetDrawerState(mark = true): void {
    this.drawerSeleccionados = [];
    this.drawerSeleccionadosMap.clear();
    this.drawerEquipamentos = [];
    this.drawerTipoSelecionado = null;
    this.drawerSearch = '';
    this.drawerPage = 1;
    this.drawerDetalheExpandidoId = null;
    this.drawerLoading = false;
    this.drawerSaving = false;
    if (mark) this.cdr.markForCheck();
  }

  selectDrawerTipo(tipo: TipoEquipamentoPosto | null): void {
    // Se selecionado o mesmo tipo, ignora
    if (tipo !== null && this.drawerTipoSelecionado?.tipoEquipamentoId === tipo.tipoEquipamentoId) {
      return;
    }

    // Se selecionar "Todos" (null), limpa a seleção anterior para reabilitar os outros
    if (tipo === null) {
      this.drawerTipoSelecionado = null;
      this.drawerEquipamentos = [];
      this.drawerSeleccionados = [];
      this.drawerSeleccionadosMap.clear();
      this.drawerSearch = '';
      this.drawerPage = 1;
      this.cdr.markForCheck();
      return;
    }

    // Caso selecione um novo tipo válido
    this.drawerTipoSelecionado = tipo;
    this.onDrawerTipoChange();
  }

  onDrawerTipoChange(): void {
    this.drawerEquipamentos = [];
    this.drawerSeleccionados = [];
    this.drawerSeleccionadosMap.clear();
    this.drawerSearch = '';
    this.drawerPage = 1;
    this.drawerDetalheExpandidoId = null;

    if (this.drawerTipoSelecionado) {
      this.loadEquipamentosDoTipo(this.drawerTipoSelecionado.tipoEquipamentoId);
    } else {
      this.cdr.markForCheck();
    }
  }

  loadEquipamentosDoTipo(tipoId: number): void {
    this.drawerLoading = true;
    this.drawerEquipamentos = [];
    this.cdr.detectChanges();

    this.equiService
      .getAll({ IdTipoEquipamento: tipoId, pageNumber: 1, pageSize: 500 })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.drawerLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          const associados = new Set(this.equipamentos.map((item) => item.equipamentoId));
          const list = this.extractData<EquipamentoDisponivel>(res);

          this.drawerEquipamentos = list
            .filter((equipamento) => {
              const equipTipoId = equipamento.idTipoEquipamento ?? (equipamento.tipoEquipamento as any)?.id;
              return Number(equipTipoId) === Number(tipoId);
            })
            .filter((equipamento) => {
              const isAtivo = equipamento.ativo !== false && !this.isEquipmentInactive(equipamento);
              const isBomEstado = equipamento.estado === EnumEstadoEquipamento.BOM || String(equipamento.estado).toUpperCase() === 'BOM';
              return isAtivo && isBomEstado;
            })
            .filter((equipamento) => !equipamento.idPosto || equipamento.idPosto === this.postoId)
            .filter((equipamento) => !associados.has(equipamento.id));

          this.drawerPage = 1;
          this.cdr.markForCheck();
        },
        error: () => {
          this.drawerEquipamentos = [];
          this.msg.add({
            severity: 'error',
            summary: 'Equipamentos indisponíveis',
            detail: 'Não foi possível carregar os equipamentos deste tipo.',
            life: 3500,
          });
          this.cdr.markForCheck();
        },
      });
  }

  get drawerEquipamentosFiltrados(): EquipamentoDisponivel[] {
    const query = this.normalize(this.drawerSearch);
    if (!query) return this.drawerEquipamentos;

    return this.drawerEquipamentos.filter((equipamento) =>
      [
        equipamento.codigoInterno,
        equipamento.estado,
        this.getEquipmentTitle(equipamento),
        ...(equipamento.propriedades ?? []).map((propriedade) => propriedade.valor),
      ].some((value) => this.normalize(value).includes(query)),
    );
  }

  get drawerEquipamentosPaginados(): EquipamentoDisponivel[] {
    const start = (this.drawerPage - 1) * this.drawerPageSize;
    return this.drawerEquipamentosFiltrados.slice(start, start + this.drawerPageSize);
  }

  onDrawerSearchChange(): void {
    this.drawerPage = 1;
    this.cdr.markForCheck();
  }

  onDrawerPageChange(event: { page?: number; rows?: number }): void {
    const rows = event.rows ?? this.drawerPageSize;
    this.drawerPage = rows !== this.drawerPageSize ? 1 : (event.page ?? 0) + 1;
    this.drawerPageSize = rows;
    this.cdr.markForCheck();
  }

  toggleEquipmentDetails(equipamentoId: number): void {
    this.drawerDetalheExpandidoId = this.drawerDetalheExpandidoId === equipamentoId
      ? null
      : equipamentoId;
    this.cdr.markForCheck();
  }

  isEquipmentDetailOpen(equipamentoId: number): boolean {
    return this.drawerDetalheExpandidoId === equipamentoId;
  }

  onEquipmentImageError(equipamento: EquipamentoDisponivel): void {
    equipamento.fotoUrl = '';
    this.cdr.markForCheck();
  }

  getEquipmentPropertyLabel(propertyName: string): string {
    return this.formatLabel(propertyName);
  }

  formatEquipmentPropertyValue(property: EquipamentoPropriedadeValor): string {
    if (property.tipoDado === 4) return this.formatDate(property.valor);
    return property.valor;
  }

  formatCurrency(value?: number): string {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('pt-AO', {
      style: 'currency',
      currency: 'AOA',
      maximumFractionDigits: 2,
    }).format(value);
  }

  getEquipmentKind(equipamento?: EquipamentoDisponivel | null): EquipmentKind {
    const tipo = this.normalize(
      equipamento?.tipoEquipamento?.designacao ??
      this.drawerTipoSelecionado?.tipoEquipamentoDescricao,
    );
    if (tipo.includes('viatura') || tipo.includes('veiculo') || tipo.includes('carro')) return 'vehicle';
    if (tipo.includes('arma') || tipo.includes('pistola') || tipo.includes('revolver')) return 'weapon';
    return 'other';
  }

  getEquipamentoProperty(equipamento: EquipamentoDisponivel, propertyName: string): string {
    const key = this.normalizePropertyName(propertyName);
    return equipamento.propriedades?.find(
      (property) => this.normalizePropertyName(property.nome) === key,
    )?.valor?.trim() ?? '';
  }

  getEquipmentTitle(equipamento: EquipamentoDisponivel): string {
    const marca = this.getEquipamentoProperty(equipamento, 'MARCA') || equipamento.marca || '';
    const modelo = this.getEquipamentoProperty(equipamento, 'MODELO') || equipamento.modelo || '';
    const title = [marca, modelo].filter(Boolean).join(' · ');
    if (title) return title;

    if (this.getEquipmentKind(equipamento) === 'weapon') {
      return this.getEquipamentoProperty(equipamento, 'TIPO') || 'Arma sem identificação';
    }
    return equipamento.tipoEquipamento?.designacao || `Equipamento ${equipamento.id}`;
  }

  getEquipmentDetails(equipamento: EquipamentoDisponivel): EquipmentDetail[] {
    const kind = this.getEquipmentKind(equipamento);
    const definitions = kind === 'vehicle'
      ? [
          ['ri-article-line', 'Matrícula', 'MATRICULA'],
          ['ri-calendar-line', 'Ano', 'ANO_FABRICO'],
          ['ri-palette-line', 'Cor', 'COR'],
        ]
      : kind === 'weapon'
        ? [
            ['ri-hashtag', 'Série', 'NUMERO_SERIE'],
            ['ri-focus-3-line', 'Calibre', 'CALIBRE'],
            ['ri-price-tag-3-line', 'Tipo', 'TIPO'],
          ]
        : [
            ['ri-stack-line', 'Quantidade', 'QUANTIDADE'],
            ['ri-calendar-line', 'Aquisição', 'DATA_AQUISICAO'],
          ];

    const details = definitions
      .map(([icon, label, property]) => ({
        icon,
        label,
        value: property === 'QUANTIDADE'
          ? String(equipamento.quantidade ?? '')
          : property === 'DATA_AQUISICAO'
            ? this.formatDate(equipamento.dataAquisicao)
            : this.getEquipamentoProperty(equipamento, property),
      }))
      .filter((detail) => !!detail.value && detail.value !== '0');

    if (equipamento.estado) {
      details.push({ icon: 'ri-checkbox-circle-line', label: 'Estado', value: this.formatTipoEquipamento(equipamento.estado) });
    }
    return details.slice(0, 4);
  }

  isEquipmentInactive(equipamento: EquipamentoDisponivel): boolean {
    return equipamento.estado === EnumEstadoEquipamento.INATIVO
      || String(equipamento.estado).toUpperCase() === 'INATIVO';
  }

  getEquipmentStateLabel(equipamento: EquipamentoDisponivel): string {
    const state = equipamento.estado;
    if (state === EnumEstadoEquipamento.BOM || String(state).toUpperCase() === 'BOM') return 'Bom';
    if (state === EnumEstadoEquipamento.DESTRUIDO || String(state).toUpperCase() === 'DESTRUIDO') return 'Destruído';
    if (this.isEquipmentInactive(equipamento)) return 'Inativo';
    return 'Estado não definido';
  }

  isEquipamentoSeleccionado(equipamento: EquipamentoDisponivel): boolean {
    return this.drawerSeleccionadosMap.has(equipamento.id);
  }

  toggleEquipamento(equipamento: EquipamentoDisponivel): void {
    if (this.drawerSeleccionadosMap.has(equipamento.id)) {
      this.removerSeleccionado(equipamento.id);
      return;
    }
    if (this.limiteAtingido) {
      this.msg.add({
        severity: 'warn',
        summary: 'Necessidade já coberta',
        detail: 'A quantidade selecionada já cobre este tipo de equipamento.',
        life: 3000,
      });
      return;
    }

    const item: NecessidadeItem = { equipamentoId: equipamento.id, quantidade: 1, equipamento };
    this.drawerSeleccionados = [...this.drawerSeleccionados, item];
    this.drawerSeleccionadosMap.set(equipamento.id, item);
    this.cdr.markForCheck();
  }

  getSeleccionadoQtd(equipamentoId: number): number {
    return this.drawerSeleccionadosMap.get(equipamentoId)?.quantidade ?? 1;
  }

  getMaxParaItem(equipamentoId: number): number {
    if (!this.drawerTipoSelecionado) return 1;
    const tipoId = this.drawerTipoSelecionado.tipoEquipamentoId;
    const necessario = this.tiposUnicos.find((tipo) => tipo.tipoEquipamentoId === tipoId)?.quantidade ?? 0;
    const jaAlocado = this.coberturaPorTipoMap.get(tipoId) ?? 0;
    const outrosTotal = this.drawerSeleccionados
      .filter((item) => item.equipamentoId !== equipamentoId)
      .reduce((sum, item) => sum + item.quantidade, 0);
    const equipamento = this.drawerSeleccionadosMap.get(equipamentoId)?.equipamento
      ?? this.drawerEquipamentos.find((item) => item.id === equipamentoId);
    const limitePorAtivo = equipamento && this.getEquipmentKind(equipamento) === 'other'
      ? Math.max(1, equipamento.quantidade ?? 1)
      : 1;
    return Math.max(1, Math.min(Math.max(0, necessario - jaAlocado - outrosTotal), limitePorAtivo));
  }

  get limiteAtingido(): boolean {
    if (!this.drawerTipoSelecionado) return false;
    const cobertura = this.getCoberturaDoTipo(this.drawerTipoSelecionado.tipoEquipamentoId);
    return cobertura.alocado + this.totalSeleccionadoQtd >= cobertura.necessario;
  }

  get totalSeleccionadoQtd(): number {
    return this.drawerSeleccionados.reduce((sum, item) => sum + item.quantidade, 0);
  }

  get drawerRemaining(): number {
    if (!this.drawerTipoSelecionado) return 0;
    const cobertura = this.getCoberturaDoTipo(this.drawerTipoSelecionado.tipoEquipamentoId);
    return Math.max(0, cobertura.necessario - cobertura.alocado - this.totalSeleccionadoQtd);
  }

  get drawerSelectionHelp(): string {
    if (!this.drawerTipoSelecionado) return 'Escolha um tipo para consultar os equipamentos disponíveis.';
    if (this.drawerRemaining === 0) return 'A seleção atual já cobre a quantidade necessária deste tipo.';

    const nome = this.normalize(this.drawerTipoSelecionado.tipoEquipamentoDescricao);
    const unidade = nome.includes('viatura') || nome.includes('veiculo')
      ? 'viatura'
      : nome.includes('arma')
        ? 'arma'
        : 'equipamento';
    return `Selecione mais ${this.drawerRemaining} ${unidade}${this.drawerRemaining === 1 ? '' : 's'} para completar a necessidade.`;
  }

  get drawerCoverageAfterSelection(): number {
    if (!this.drawerTipoSelecionado) return 0;
    const cobertura = this.getCoberturaDoTipo(this.drawerTipoSelecionado.tipoEquipamentoId);
    return Math.min(cobertura.alocado + this.totalSeleccionadoQtd, cobertura.necessario);
  }

  updateQuantidadeSeleccionado(equipamentoId: number, value: number): void {
    const item = this.drawerSeleccionadosMap.get(equipamentoId);
    if (!item) return;
    item.quantidade = Math.min(Math.max(1, Number(value) || 1), this.getMaxParaItem(equipamentoId));
    this.drawerSeleccionados = [...this.drawerSeleccionados];
    this.cdr.markForCheck();
  }

  removerSeleccionado(equipamentoId: number): void {
    this.drawerSeleccionados = this.drawerSeleccionados.filter((item) => item.equipamentoId !== equipamentoId);
    this.drawerSeleccionadosMap.delete(equipamentoId);
    this.cdr.markForCheck();
  }

  salvarNecessidade(): void {
    if (!this.drawerSeleccionados.length || this.drawerSaving) return;
    this.drawerSaving = true;
    this.cdr.markForCheck();

    const payload = this.drawerSeleccionados.map(({ equipamentoId, quantidade }) => ({ equipamentoId, quantidade }));
    this.postoSvc
      .adicionarNecessidade(this.postoId, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.drawerSaving = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: () => {
          this.msg.add({ severity: 'success', summary: 'Necessidade registada', detail: 'Os equipamentos foram associados ao posto.' });
          this.showDrawer = false;
          this.loadEquiPostoNecessidade();
        },
        error: () => this.msg.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao guardar a necessidade.' }),
      });
  }

  removerEquipamento(item: EquipamentoNecessidade): void {
    const label = item.nomeEquipamento || `EQ-${item.equipamentoId}`;
    this.confirm.confirm({
      header: 'Remover equipamento',
      message: `Remover a necessidade do equipamento "${label}" deste posto?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Remover',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.postoSvc
          .removerEquipamentoNecessidade(item.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msg.add({ severity: 'success', summary: 'Removido', detail: `"${label}" removido.` });
              this.loadEquiPostoNecessidade();
            },
            error: () => this.msg.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao remover.' }),
          });
      },
    });
  }

  getIconForTipo(tipoId: number): string {
    const tipoGlobal = this.tiposEquipamentos.find((tipo) => tipo.id === tipoId);
    if (tipoGlobal?.icon) return tipoGlobal.icon;
    const tipoPosto = this.tiposDoPosto.find((tipo) => tipo.tipoEquipamentoId === tipoId);
    if (tipoPosto) return this.getIconByNome(tipoPosto.tipoEquipamentoDescricao);
    const necessidade = this.equipamentos.find((equipamento) => equipamento.tipoEquipamentoId === tipoId);
    return necessidade ? this.getIconByNome(necessidade.tipoEquipamento) : 'ri-tools-line';
  }

  private getIconByNome(nome: string): string {
    const value = this.normalize(nome);
    if (value.includes('arma') || value.includes('pistol') || value.includes('ak')) return 'ri-shield-cross-line';
    if (value.includes('viatura') || value.includes('veiculo') || value.includes('carro')) return 'ri-car-line';
    if (value.includes('radio')) return 'ri-radio-line';
    if (value.includes('camera')) return 'ri-camera-line';
    if (value.includes('colete')) return 'ri-shield-line';
    if (value.includes('farda')) return 'ri-t-shirt-line';
    if (value.includes('kit') || value.includes('socorr')) return 'ri-first-aid-kit-line';
    if (value.includes('detector')) return 'ri-scan-line';
    if (value.includes('moto')) return 'ri-motorbike-line';
    return 'ri-tools-line';
  }

  getTipoNome(id: number): string {
    return this.tiposEquipamentos.find((tipo) => tipo.id === id)?.designacao || 'Tipo desconhecido';
  }

  formatTipoEquipamento(tipo?: unknown): string {
    return tipo !== null && tipo !== undefined && tipo !== ''
      ? this.formatLabel(String(tipo))
      : '';
  }

  formatDate(value?: string): string {
    if (!value) return '';
    const date = new Date(`${value.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('pt-AO').format(date);
  }

  private formatLabel(value: string): string {
    return value.replace(/_/g, ' ').toLocaleLowerCase('pt').replace(/\b\p{L}/gu, (letter) => letter.toLocaleUpperCase('pt'));
  }

  private normalize(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('pt')
      .trim();
  }

  private normalizePropertyName(value: string): string {
    return this.normalize(value).replace(/[\s-]+/g, '_');
  }

  trackById(_: number, item: { id: number }): number {
    return item.id;
  }

  trackByTipoId(_: number, tipo: TipoEquipamentoPosto): number {
    return tipo.tipoEquipamentoId;
  }

  trackByEqId(_: number, item: NecessidadeItem): number {
    return item.equipamentoId;
  }
}